import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getQuizHistory, getQuizStatus, getDailyQuiz, submitQuiz } from '../services/quizService';
import { useNavigate } from 'react-router-dom';
import { FaFire, FaClock, FaCheckCircle, FaTimesCircle, FaTrophy, FaLightbulb, FaChartPie, FaWhatsapp, FaLinkedin, FaLink, FaCopy, FaDownload } from 'react-icons/fa';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { ActivityCalendar } from 'react-activity-calendar';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import html2canvas from 'html2canvas';

ChartJS.register(ArcElement, Tooltip, Legend);

const DailyQuiz = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const badgeRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gameState, setGameState] = useState('start'); // start, playing, result
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({}); // { questionId: option }
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(user?.streak || 0);
  const [selectedCategory, setSelectedCategory] = useState('MIX');
  const [historyData, setHistoryData] = useState([
    { date: new Date().toISOString().split('T')[0], count: 0, level: 0 }
  ]);
  const [completedCategories, setCompletedCategories] = useState([]);
  const [newBadge, setNewBadge] = useState(null);

  const categories = [
    { id: 'APTITUDE', label: 'Aptitude' },
    { id: 'CORE_CS', label: 'Core CS' },
    { id: 'GENERAL', label: 'General' },
    { id: 'MIX', label: 'Mix Challenge' }
  ];

  // Fetch Quiz History & Status
  useEffect(() => {
    const fetchData = async () => {
        if(user?._id || user?.id) {
            try {
                const uId = user.id || user._id;
                
                // Fetch History
                const historyData = await getQuizHistory(uId);
                if (historyData && historyData.length > 0) {
                    setHistoryData(historyData);
                }

                // Fetch Daily Status
                const statusData = await getQuizStatus(uId);
                if (statusData?.completedCategories) {
                    setCompletedCategories(statusData.completedCategories);
                }
            } catch (err) {
                console.error("Failed to load user data", err);
            }
        }
    }
    fetchData();
  }, [user]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
        const data = await getDailyQuiz(selectedCategory);
        if(data.length === 0) {
            setError("No questions available for this category today.");
            setLoading(false);
            return;
        }
        setQuestions(data);
        setGameState('playing');
    } catch (err) {
        console.error("Error fetching quiz", err);
        setError('Failed to load quiz. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  const handleOptionSelect = (option) => {
    const currentQ = questions[currentQuestionIndex];
    if (selectedAnswers[currentQ.id]) return; // Prevent changing answer

    setSelectedAnswers(prev => ({
      ...prev,
      [currentQ.id]: option
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmitQuiz();
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      setLoading(true);
      const answersPayload = Object.entries(selectedAnswers).map(([qId, opt]) => ({
        questionId: parseInt(qId),
        selectedOption: opt
      }));

      // Ensure user ID is valid
      const uId = user?.id || user?._id;
      if (!uId) {
        setError("User session invalid. Please relogin.");
        setLoading(false);
        return;
      }

      const data = await submitQuiz({
        userId: uId,
        answers: answersPayload,
        category: selectedCategory
      });

      setResult(data);
      setStreak(data.streak); 
      setGameState('result');
      
      setCompletedCategories(prev => [...prev, selectedCategory]);
      
      setUser({
        ...user,
        xp: (user.xp || 0) + data.xpEarned,
        streak: data.streak,
      });

      if (data.newBadge) {
        setNewBadge(data.newBadge);
      }

    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
         setError(err.response.data.message);
      } else {
         setError('Failed to submit quiz.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    const shareUrl = "https://gaplify.in/daily-quiz";
    const text = `Check out my score of ${result?.score || 0}/10 on Gaplify Daily Quiz! Can you beat me? 🚀 #Gaplify #DailyQuiz`;
    
    if (platform === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`, '_blank');
    } else if (platform === 'linkedin') {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText("https://gaplify.in/daily-quiz");
    alert("Link copied to clipboard!"); 
  };

  const handleDownloadBadge = async () => {
    if (badgeRef.current) {
        try {
            const canvas = await html2canvas(badgeRef.current, {
                backgroundColor: null, // Transparent background if possible, or matches style
                scale: 2 // Higher resolution
            });
            const link = document.createElement('a');
            link.download = `Gaplify-Badge-${newBadge?.name || 'Achievement'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error("Failed to download badge", err);
        }
    }
  };


  if (loading && gameState === 'playing') {
      // Loading spinner during transition
  }

  if (loading && !result) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
        <div className="bg-red-50 p-6 rounded-2xl max-w-lg w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Oops!</h2>
            <p className="text-gray-700">{error}</p>
            <button 
                onClick={() => navigate('/')}
                className="mt-6 px-6 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition"
            >
                Back to Home
            </button>
        </div>
      </div>
    );
  }



  // --- START SCREEN ---
  if (gameState === 'start') {
    return (
      <div className="container-responsive min-h-[85vh] py-12 flex flex-col items-center">
        
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Left Card: Start Quiz & Categories */}
            <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden p-8 lg:p-10 border border-gray-100 flex flex-col justify-between"
            >
                <div>
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center text-orange-600 shadow-sm">
                            <FaFire className="w-8 h-8" />
                        </div>
                        <h1 className="text-3xl font-display font-bold text-gray-900">
                            Daily Challenge
                        </h1>
                    </div>
                    
                    <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                        Master your skills! Select a topic and keep your knowledge sharp.
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-8">
                        {categories.map((cat) => {
                            const isCompleted = completedCategories.includes(cat.id);
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => !isCompleted && setSelectedCategory(cat.id)}
                                    disabled={isCompleted}
                                    className={`relative px-4 py-3 rounded-xl font-medium text-sm transition-all border-2 ${
                                        selectedCategory === cat.id
                                        ? 'bg-primary-50 border-primary-500 text-primary-700 shadow-md transform scale-105'
                                        : isCompleted
                                            ? 'bg-gray-100 border-gray-100 text-gray-400 cursor-not-allowed opacity-75' 
                                            : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {cat.label}
                                    {isCompleted && (
                                        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                                            Done
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <button 
                    onClick={handleStart}
                    disabled={loading || completedCategories.includes(selectedCategory)}
                    className={`w-full py-4 text-white text-xl font-bold rounded-2xl shadow-lg transition-all duration-300 transform flex items-center justify-center space-x-2 ${
                        loading || completedCategories.includes(selectedCategory)
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:shadow-xl hover:scale-[1.02]'
                    }`}
                >
                    <span>
                        {loading 
                            ? 'Loading...' 
                            : completedCategories.includes(selectedCategory) 
                                ? 'Completed for Today' 
                                : 'Start Quiz'
                        }
                    </span>
                    {!loading && !completedCategories.includes(selectedCategory) && <FaFire className="w-5 h-5" />}
                    {completedCategories.includes(selectedCategory) && <FaCheckCircle className="w-5 h-5" />}
                </button>
            </motion.div>

            {/* Right Card: Stats & Badges */}
            <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden p-8 lg:p-10 border border-gray-100 flex flex-col"
            >
                <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xl font-bold text-gray-900">Your Progress</h3>
                    <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold uppercase tracking-wider">
                        Level {Math.floor((user?.xp || 0) / 100) + 1}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 rounded-2xl p-5 text-center border border-gray-100">
                         <div className="text-4xl font-display font-bold text-primary-600 mb-1">{user?.streak || 0}</div>
                         <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Day Streak</div>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-5 text-center border border-gray-100">
                         <div className="text-4xl font-display font-bold text-indigo-600 mb-1">{user?.xp || 0}</div>
                         <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Total XP</div>
                    </div>
                </div>

                <div className="flex-1 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <FaTrophy className="w-24 h-24" />
                    </div>
                    <div className="relative z-10">
                        <h4 className="font-bold text-lg mb-2">Next Milestone</h4>
                        <div className="flex justify-between text-sm text-gray-400 mb-2">
                            <span>{user?.xp || 0} XP</span>
                            <span>{Math.ceil((user?.xp || 1) / 100) * 100} XP</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                             <div 
                                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                                style={{ width: `${((user?.xp || 0) % 100)}%` }}
                             ></div>
                        </div>
                        <p className="text-xs text-gray-400 mt-4">
                            Keep practicing to unlock new badges and climb the leaderboard!
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>

 

      </div>
    );
  }

  // --- PLAYING SCREEN ---
  if (gameState === 'playing') {
    const question = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isSelected = selectedAnswers[question.id];

    return (
      <div className="container-responsive py-8 lg:py-16 max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex justify-between text-sm font-medium text-gray-500 mb-2">
            <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
            <span className="text-primary-600 font-bold">{question.category}</span>
          </div>
          <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <motion.div 
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 lg:p-10"
        >
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-8 leading-relaxed">
            {question.question}
          </h2>

          <div className="grid gap-4">
            {question.options.map((opt, idx) => {
              const isAnswered = !!selectedAnswers[question.id];
              const isSelected = selectedAnswers[question.id] === opt;
              const isCorrect = opt === question.correct;
              
              let buttonStyle = "border-gray-100 hover:border-gray-300 hover:bg-gray-50";
              let icon = null;
              let letterStyle = "bg-gray-100 text-gray-500 group-hover:bg-gray-200";

              if (isAnswered) {
                if (isCorrect) {
                   buttonStyle = "border-green-500 bg-green-50 text-green-800";
                   letterStyle = "bg-green-500 text-white";
                   icon = <FaCheckCircle className="text-green-500 w-6 h-6" />;
                } else if (isSelected) {
                   buttonStyle = "border-red-500 bg-red-50 text-red-800";
                   letterStyle = "bg-red-500 text-white";
                   icon = <FaTimesCircle className="text-red-500 w-6 h-6" />;
                } else {
                   buttonStyle = "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed";
                }
              } else if (isSelected) {
                  // Fallback if not answered but selected (shouldn't happen with new logic, but safe to keep for transition)
                   buttonStyle = "border-primary-500 bg-primary-50 text-primary-800";
                   letterStyle = "bg-primary-500 text-white";
                   icon = <FaCheckCircle className="text-primary-500 w-6 h-6" />;
              }

              return (
              <button
                key={idx}
                onClick={() => handleOptionSelect(opt)}
                disabled={isAnswered}
                className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-between group ${buttonStyle}`}
              >
                <div className="flex items-center">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 text-sm font-bold transition-colors ${letterStyle}`}>
                        {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="font-medium text-lg">{opt}</span>
                </div>
                {icon}
              </button>
            )})}
          </div>

          <div className="mt-10 flex justify-end">
            <button
              disabled={!isSelected}
              onClick={handleNext}
              className={`px-8 py-3 rounded-xl font-bold text-white transition-all duration-300 ${
                !isSelected 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-primary-600 hover:bg-primary-700 hover:scale-105 shadow-md'
              }`}
            >
              {currentQuestionIndex === questions.length - 1 ? 'Submit Quiz' : 'Next Question'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- RESULT SCREEN ---
  if (gameState === 'result' && result) {
    const percentage = (result.score / 10) * 100;
    const doughnutData = {
        labels: ['Correct', 'Incorrect'],
        datasets: [{
            data: [result.score, 10 - result.score],
            backgroundColor: ['#4F46E5', '#E5E7EB'], // Primary-600, Gray-200
            borderWidth: 0,
        }]
    };

    return (
      <div className="container-responsive py-12 max-w-5xl mx-auto">
        <Confetti numberOfPieces={percentage > 50 ? 200 : 0} recycle={false} />
        
        <div className="text-center mb-10">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-block"
          >
             <h1 className="text-4xl font-display font-bold text-gray-900 mb-2">Quiz Complete!</h1>
             <p className="text-gray-500 text-xl">You earned <span className="font-bold text-yellow-500">+{result.xpEarned} XP</span> today</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Score Card */}
            <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 flex flex-col items-center justify-center relative overflow-hidden"
            >
                 <div className="absolute top-0 right-0 p-6 opacity-10">
                    <FaTrophy className="w-32 h-32 text-primary-500" />
                 </div>
                 
                 <div className="relative w-48 h-48 mb-6">
                    <Doughnut data={doughnutData} options={{ maintainAspectRatio: false, cutout: '75%', plugins: { legend: { display: false }, tooltip: { enabled: false } } }} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-800">
                        <span className="text-5xl font-bold font-display">{percentage}%</span>
                        <span className="text-sm font-medium text-gray-400">Accuracy</span>
                    </div>
                 </div>
                 
                 <div className="grid grid-cols-2 gap-8 w-full">
                    <div className="text-center p-4 bg-gray-50 rounded-2xl">
                        <div className="text-2xl font-bold text-green-600">{result.score}</div>
                        <div className="text-sm text-gray-500 font-medium">Correct</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-2xl">
                        <div className="text-2xl font-bold text-red-500">{10 - result.score}</div>
                        <div className="text-sm text-gray-500 font-medium">Wrong</div>
                    </div>
                 </div>
            </motion.div>

            {/* Analysis Card */}
            <motion.div 
                 initial={{ y: 20, opacity: 0 }}
                 animate={{ y: 0, opacity: 1 }}
                 transition={{ delay: 0.2 }}
                 className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
            >
                <div className="flex items-center mb-6">
                    <FaChartPie className="text-primary-600 w-6 h-6 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900">Performance Breakdown</h3>
                </div>

                <div className="space-y-6">
                    {Object.entries(result.categoryBreakdown)
                        .filter(([_, stats]) => stats.total > 0)
                        .map(([cat, stats]) => (
                        <div key={cat}>
                            <div className="flex justify-between text-sm font-medium mb-1">
                                <span className="text-gray-700 capitalize">{cat.replace('_', ' ')}</span>
                                <span className="text-gray-500">{stats.correct}/{stats.total}</span>
                            </div>
                            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full rounded-full ${
                                        (stats.correct / stats.total) > 0.7 ? 'bg-green-500' : 
                                        (stats.correct / stats.total) > 0.4 ? 'bg-yellow-400' : 'bg-red-400' 
                                    }`}
                                    style={{ width: `${stats.total > 0 ? (stats.correct / stats.total) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 p-4 bg-blue-50 rounded-2xl flex items-start">
                    <FaLightbulb className="text-blue-600 w-5 h-5 mt-1 mr-3 flex-shrink-0" />
                    <div>
                        <h4 className="font-bold text-blue-800 text-sm mb-1">Tip for Improvement</h4>
                        <p className="text-blue-700 text-sm leading-relaxed">
                            {result.suggestion}
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>

        {/* Review Mistakes Section */}
        {result.answers && result.answers.some(a => !a.isCorrect) && (
            <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
            >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <FaLightbulb className="text-yellow-500 mr-2" />
                    Learning Review
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {result.answers.filter(a => !a.isCorrect).map((ans, idx) => (
                        <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-red-100 hover:shadow-md transition-shadow">
                            <p className="font-bold text-gray-800 mb-3">{ans.questionText}</p>
                            
                            <div className="flex flex-col space-y-2 mb-4">
                                <div className="flex items-center text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                                    <FaTimesCircle className="mr-2 flex-shrink-0" />
                                    <span>You: {ans.selectedOption}</span>
                                </div>
                                <div className="flex items-center text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                                    <FaCheckCircle className="mr-2 flex-shrink-0" />
                                    <span>Correct: {ans.correctOption}</span>
                                </div>
                            </div>

                            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
                                <span className="font-bold text-gray-700 block mb-1">Explanation:</span>
                                {ans.explanation || "No explanation available."}
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        )}

        {/* Challenge Friends Section */}
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden text-center"
        >
             <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-2">Challenge Your Friends!</h3>
                <p className="text-purple-100 mb-6 max-w-lg mx-auto">
                    Show off your score and see if your friends can beat you.
                </p>

                <div className="flex flex-wrap justify-center gap-4">
                    <button
                        onClick={() => handleShare('whatsapp')}
                        className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:-translate-y-0.5"
                    >
                        <FaWhatsapp className="w-5 h-5" />
                        <span>WhatsApp</span>
                    </button>
                     <button
                        onClick={() => handleShare('linkedin')}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:-translate-y-0.5"
                    >
                        <FaLinkedin className="w-5 h-5" />
                        <span>LinkedIn</span>
                    </button>
                     <button
                        onClick={handleCopyLink}
                        className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-bold transition-all border border-white/20"
                    >
                        <FaLink className="w-5 h-5" />
                        <span>Copy Link</span>
                    </button>
                </div>
             </div>

             {/* Decorative circles */}
             <div className="absolute top-0 left-0 -ml-10 -mt-10 w-40 h-40 bg-white opacity-10 rounded-full blur-2xl"></div>
             <div className="absolute bottom-0 right-0 -mr-10 -mb-10 w-40 h-40 bg-purple-400 opacity-20 rounded-full blur-2xl"></div>
        </motion.div>

        <div className="text-center">
            <button
                onClick={() => {
                    setGameState('start');
                    setResult(null);
                    setQuestions([]);
                    setCurrentQuestionIndex(0);
                    setSelectedAnswers({});
                    setSelectedCategory('MIX');
                    setNewBadge(null);
                }}
                className="px-8 py-3 bg-gray-900 text-white rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
                Back to Dashboard
            </button>
        </div>

        {/* Badge Unlocked Modal */}
        <AnimatePresence>
            {newBadge && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <Confetti numberOfPieces={500} recycle={false} />
                    <motion.div
                        ref={badgeRef}
                        initial={{ scale: 0.5, y: 50 }}
                        animate={{ scale: 1, y: 0 }}
                        className="bg-white rounded-3xl p-10 max-w-md w-full text-center relative overflow-hidden shadow-2xl border-4 border-yellow-400"
                    >
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-400 to-orange-500"></div>

                        <div className="mb-6 relative">
                            <div className="absolute inset-0 bg-yellow-100 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                            <div className="text-8xl relative z-10 animate-bounce">{newBadge.icon}</div>
                        </div>

                        <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">Badge Unlocked!</h2>
                        <h3 className="text-xl font-bold text-yellow-600 mb-4">{newBadge.name}</h3>
                        <p className="text-gray-600 mb-8 text-lg">{newBadge.description}</p>

                        <div className="flex flex-col gap-3">
                             <button
                                onClick={() => {
                                    const shareUrl = "https://gaplify.in/daily-quiz";
                                    const text = `Excited to share that I've unlocked the "${newBadge.name}" badge on Gaplify! 🏆\n\n${newBadge.description}\n\nI'm challenging myself to improve my skills daily. Join me and test your knowledge! 👇\n${shareUrl}\n\n#Gaplify #DailyQuiz #LearningJourney #AchievementUnlocked`;
                                    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`, '_blank');
                                }}
                                className="w-full py-3 bg-[#0077b5] text-white rounded-xl font-bold shadow-lg hover:bg-[#006396] transition-all flex items-center justify-center"
                            >
                                <FaLinkedin className="mr-2 text-xl" /> Share on LinkedIn
                            </button>
                             <button 
                                onClick={handleDownloadBadge}
                                className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg hover:bg-purple-700 transition-all flex items-center justify-center"
                            >
                                <FaDownload className="mr-2 text-xl" /> Save Badge
                            </button>
                            <button
                                onClick={() => setNewBadge(null)}
                                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                            >
                                Awesome!
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
};

export default DailyQuiz;
